import { Authorization, Token, UserInfo } from '@/constants/authorization';
import { shouldClearUserSession } from './api-error';
import { getSearchValue } from './common-util';
const KeySet = [Authorization, Token, UserInfo];

// Authentication is intentionally scoped to a browser tab. Persisting it in
// localStorage made a login in one tab overwrite every other tab on the same
// origin, which could silently turn an employee session into an administrator
// session. Language remains a browser preference and therefore stays in
// localStorage.
const authenticationStorage = sessionStorage;

const storage = {
  getAuthorization: () => {
    return authenticationStorage.getItem(Authorization);
  },
  getToken: () => {
    return authenticationStorage.getItem(Token);
  },
  getUserInfo: () => {
    return authenticationStorage.getItem(UserInfo);
  },
  getUserInfoObject: () => {
    const userInfo = authenticationStorage.getItem(UserInfo);
    return userInfo ? JSON.parse(userInfo) : undefined;
  },
  setAuthorization: (value: string) => {
    authenticationStorage.setItem(Authorization, value);
  },
  setToken: (value: string) => {
    authenticationStorage.setItem(Token, value);
  },
  setUserInfo: (value: string | Record<string, unknown>) => {
    let valueStr = typeof value !== 'string' ? JSON.stringify(value) : value;
    authenticationStorage.setItem(UserInfo, valueStr);
  },
  setItems: (pairs: Record<string, string>) => {
    Object.entries(pairs).forEach(([key, value]) => {
      authenticationStorage.setItem(key, value);
    });
  },
  removeAuthorization: () => {
    authenticationStorage.removeItem(Authorization);
  },
  removeAll: () => {
    KeySet.forEach((x) => {
      authenticationStorage.removeItem(x);
    });
  },
  setLanguage: (lng: string) => {
    localStorage.setItem('lng', lng);
  },
  getLanguage: (): string => {
    return localStorage.getItem('lng') as string;
  },
};

export const getAuthorization = () => {
  const isPublishedPage = window.location.pathname === '/next-chat/share';
  if (isPublishedPage) return '';
  const auth = getSearchValue('auth');
  if (auth) return `Bearer ${auth}`;
  return storage.getAuthorization() || '';
};

export const isSameOriginUrl = (url: string) => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * 打开需要登录态的同源页面，并把当前标签页身份精确传递给新标签页。
 *
 * 身份信息仍保存在 sessionStorage 中，不进入 URL、Referer 或浏览器历史；
 * 完成传递后立即断开 opener，避免新页面反向控制来源页面。
 */
export const openAuthenticatedWindow = (url: string) => {
  if (!isSameOriginUrl(url)) return false;

  const resolvedUrl = new URL(url, window.location.href).href;
  const childWindow = window.open('about:blank', '_blank');
  if (!childWindow) return false;

  try {
    KeySet.forEach((key) => {
      const value = authenticationStorage.getItem(key);
      if (value) childWindow.sessionStorage.setItem(key, value);
    });
    childWindow.opener = null;
    childWindow.location.replace(resolvedUrl);
    return true;
  } catch {
    childWindow.close();
    return false;
  }
};

export default storage;

let redirectingToLogin = false;

export function redirectToLogin() {
  if (window.location.pathname === '/login' || redirectingToLogin) return;
  redirectingToLogin = true;
  window.location.href = location.origin + `/login`;
}

/**
 * Handles authentication failures for streaming/file requests that use the
 * browser fetch API instead of the shared Axios/umi request clients.
 */
export async function redirectToLoginOnUnauthorizedResponse(
  response: Response,
) {
  if (response.status !== 401) return false;

  let data: Record<string, unknown> = { code: 401 };
  try {
    data = { ...data, ...(await response.clone().json()) };
  } catch {
    // Reverse proxies can return an HTML/plain-text 401. The HTTP status is
    // sufficient to identify an expired session on a protected page.
  }
  if (!shouldClearUserSession(data, response.status)) return false;

  storage.removeAll();
  redirectToLogin();
  return true;
}
